import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import InputBase from 'components/InputBase';
import dtHelper from 'helpers/dateTimeHelper';

function pad(num) {
  const s = Number(num).toString(10);
  if (s.length < 2) {
    return `0${s}`;
  }
  return s;
}

const StyledDateInput = styled(InputBase).attrs({ noBorder: true })`
`;

const InputContainer = styled.div`
  display: flex;
  width: 240px;
  align-items: center;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing * 2}px ;
`;

const StyledSelect = styled.select`
  outline: none;
  background-color: transparent;
  border: none;
  border-bottom: ${({ error, theme }) => (error ? theme.palette.input.error : theme.palette.input.text)} solid 2px;
  padding-bottom: ${({ theme }) => theme.spacing}px;
  font-size: 1.5rem;

  &:focus {
    border-bottom-color: ${({ theme }) => theme.palette.input.focus};
  }
`;

const StyledMonthDaySelect = styled(StyledSelect)`
  width: 52px;
`;

const StyledYearSelect = styled(StyledSelect)`
  width: 80px;
`;

const DateInput = (
  {
    label, value, onChange, error, disabled, className, focus,
  },
) => {
  const classes = ['DateInput'];
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const dayInputRef = useRef();

  const daysDropDown = Array(31).fill(null).map((i, idx) => idx + 1);
  const monthDropDown = Array(12).fill(null).map((i, idx) => idx + 1);
  const currentYear = new Date(dtHelper.now()).getFullYear();
  const yearsDropDown = Array(110).fill(null).map((i, idx) => currentYear - idx);

  useEffect(() => {
    if (focus && dayInputRef.current) {
      dayInputRef.current.focus();
    }
  }, [focus, dayInputRef]);

  useEffect(() => {
    if (value) {
      const [valYear, valMonth, valDay] = value.split('-').map((s) => parseInt(s, 10));
      setYear(valYear);
      setMonth(valMonth);
      setDay(valDay);
    } else {
      setYear('');
      setMonth('');
      setDay('');
    }
  }, [value]);

  useEffect(() => {
    if (onChange) {
      onChange((year && month && day) ? `${year}-${pad(month)}-${pad(day)}` : null);
    }
  }, [onChange, day, month, year]);

  const handleDayChange = (e) => {
    setDay(e.target.value);
  };

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  if (className) {
    classes.push(className);
  }
  return (
    <StyledDateInput className={classes.join(' ')} label={label} error={error}>
      <InputContainer>
        <StyledMonthDaySelect
          placeholder="DD"
          value={day}
          disabled={disabled}
          onChange={handleDayChange}
          ref={dayInputRef}
          error={!!error}
        >
          <option value="">--</option>
          {daysDropDown.map((option) => <option key={option} value={option}>{option}</option>)}
        </StyledMonthDaySelect>
        /
        <StyledMonthDaySelect
          placeholder="MM"
          value={month}
          disabled={disabled}
          onChange={handleMonthChange}
          error={!!error}
        >
          <option value="">--</option>
          {monthDropDown.map((option) => <option key={option} value={option}>{option}</option>)}
        </StyledMonthDaySelect>
        /
        <StyledYearSelect
          placeholder="YYYY"
          value={year}
          disabled={disabled}
          onChange={handleYearChange}
          error={!!error}
        >
          <option value="">----</option>
          {yearsDropDown.map((option) => <option key={option} value={option}>{option}</option>)}
        </StyledYearSelect>
      </InputContainer>
    </StyledDateInput>
  );
};

DateInput.displayName = 'DateInput';

DateInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  focus: PropTypes.bool,
};

DateInput.defaultProps = {
  label: null,
  value: "",
  error: null,
  disabled: false,
  className: null,
  onChange: null,
  focus: false,
};

export default DateInput;
